import React, { useState, useEffect } from "react";
import FormInputs from "../../../../components/UI/FormInputs";
import dayjs from "dayjs";

// Convert Y-m-d or MM/DD/YYYY to MM/DD/YYYY for DatePicker disabledDates
const toMmddyyyy = (dateStr) => {
  if (!dateStr) return null;
  const d = dayjs(dateStr, ['YYYY-MM-DD', 'MM/DD/YYYY'], true);
  return d.isValid() ? d.format('MM/DD/YYYY') : null;
};

const ConflictResolutionPanel = ({
  conflicts,
  conflictResolutions,
  onConflictResolved,
  onBulkResolve,
  primaryPatient,
  secondaryPatient,
  mergePreview,
  visitConflictResolutions = {},
  onVisitConflictResolved,
}) => {
  const [localResolutions, setLocalResolutions] = useState(conflictResolutions || {});
  const [showMatchedList, setShowMatchedList] = useState(false);

  useEffect(() => {
    setLocalResolutions(conflictResolutions || {});
  }, [conflictResolutions]);

  const handleResolutionChange = (field, resolution) => {
    const newResolutions = { ...localResolutions, [field]: resolution };
    setLocalResolutions(newResolutions);
    onConflictResolved(field, resolution);
  };

  const handleBulkResolve = (resolution) => {
    const newResolutions = { ...localResolutions };
    if (conflicts?.employee_record_conflicts) {
      conflicts.employee_record_conflicts.forEach((conflict) => {
        newResolutions[conflict.field] = resolution;
      });
    }
    setLocalResolutions(newResolutions);
    onBulkResolve(newResolutions);
  };

  const toCamelCase = (str) => {
    if (!str || typeof str !== "string") return str;
    return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  };

  const primaryName = primaryPatient
    ? `${primaryPatient.first_name || ''} ${primaryPatient.last_name || ''}`.trim() || '—'
    : '—';
  const secondaryName = secondaryPatient
    ? `${secondaryPatient.first_name || ''} ${secondaryPatient.last_name || ''}`.trim() || '—'
    : '—';
  const primaryVisitCount = mergePreview?.primary_visits_count ?? primaryPatient?.visit_count ?? '—';
  const secondaryVisitCount = mergePreview?.secondary_visits_count ?? secondaryPatient?.visit_count ?? '—';

  // Hide company and clinic from UI; backend will use "keep primary" for them
  const FIELDS_HIDDEN_FROM_UI = ['company_id', 'clinic_id'];
  const visibleConflicts = (conflicts?.employee_record_conflicts || []).filter(
    (c) => !FIELDS_HIDDEN_FROM_UI.includes(c.field)
  );
  const conflictCount = visibleConflicts.length;
  const ALL_MERGE_FIELDS = ['firstName', 'lastName', 'dob', 'phone', 'email', 'streetAddress', 'city', 'state', 'zipCode', 'county'];
  const conflictFieldSet = new Set(
    visibleConflicts.map((c) => toCamelCase(c.field))
  );

  const isEmptyValue = (v) => {
    if (v === null || v === undefined) return true;
    if (typeof v === "string") return v.trim() === "";
    return false;
  };

  const getEmployeeFieldValue = (patient, camelFieldName) => {
    if (!patient) return null;

    // API payloads are often snake_case (street_address) while the UI uses camelCase
    const snakeCaseFieldName = camelFieldName.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);

    return patient[camelFieldName] ?? patient[snakeCaseFieldName] ?? null;
  };

  // Matched fields = fields not flagged as conflicts, but only if at least one side has a value.
  // This prevents empty-empty fields (e.g., street address / city / state) from appearing under "Matched Fields".
  const matchedFields = ALL_MERGE_FIELDS
    .filter((f) => !conflictFieldSet.has(f))
    .filter((f) => {
      const pVal = getEmployeeFieldValue(primaryPatient, f);
      const sVal = getEmployeeFieldValue(secondaryPatient, f);
      return !(isEmptyValue(pVal) && isEmptyValue(sVal));
    });

  // Debug: helps verify why fields show up under "Matched Fields"
  // (safe to remove later)
  useEffect(() => {
    try {
      console.log("[MergeCharts] Matched/conflict debug", {
        conflictFields: Array.from(conflictFieldSet.values()),
        allMergeFields: ALL_MERGE_FIELDS,
        matchedFields,
        primaryCounty: getEmployeeFieldValue(primaryPatient, "county"),
        secondaryCounty: getEmployeeFieldValue(secondaryPatient, "county"),
        countyInConflicts: conflictFieldSet.has("county"),
        primaryValuesSample: ALL_MERGE_FIELDS.reduce((acc, f) => {
          acc[f] = getEmployeeFieldValue(primaryPatient, f);
          return acc;
        }, {}),
        secondaryValuesSample: ALL_MERGE_FIELDS.reduce((acc, f) => {
          acc[f] = getEmployeeFieldValue(secondaryPatient, f);
          return acc;
        }, {}),
      });
    } catch (e) {
      // Ignore debug errors in production
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryPatient, secondaryPatient, conflicts, showMatchedList]);

  return (
    <div className="merge-resolve-section pt-3 border-top">
      <div className="card border-0">
        <div className="card-header sh_accordionheader">
          <h6 className="mb-0">Review & resolve conflicts</h6>
        </div>
        <div className="card-body p-2">
          <div className="merge-refined-mini-bar d-flex flex-wrap justify-content-between align-items-center gap-3 p-3 rounded mb-3">
            <div className="d-flex flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2" id="mergeMiniPrimary">
                <strong>Primary:</strong> {primaryName}
              </div>
              <div className="d-flex align-items-center gap-2" id="mergeMiniSecondary">
                <strong>Secondary:</strong> {secondaryName}
              </div>
            </div>
            <div className="ms-auto d-flex align-items-center gap-2">
              <span
                className={`badge rounded-pill ${conflictCount ? 'bg-danger' : 'bg-secondary'}`}
                id="mergeConflictBadge"
              >
                {conflictCount} conflict{conflictCount !== 1 ? 's' : ''}
              </span>
              <span className="badge rounded-pill bg-secondary" id="mergeMatchedBadge">
                {matchedFields.length} matched
              </span>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              id="mergeUseAllPrimary"
              onClick={() => handleBulkResolve('primary')}
            >
              Use all from Primary
            </button>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              id="mergeUseAllSecondary"
              onClick={() => handleBulkResolve('secondary')}
            >
              Use all from Secondary
            </button>
          </div>
          {matchedFields.length > 0 && (
            <div className="mb-3">
              <button
                type="button"
                className="merge-matched-toggle d-flex align-items-center justify-content-between w-100 p-2 rounded border merge-refined-toggle"
                id="mergeMatchedToggle"
                aria-expanded={showMatchedList}
                onClick={() => setShowMatchedList(!showMatchedList)}
              >
                <span id="mergeMatchedText">
                  {showMatchedList ? 'Matched fields shown.' : 'Matched fields hidden.'}
                </span>
                <span className="text-primary small" id="mergeMatchedToggleLabel">
                  {showMatchedList ? 'Hide' : 'Show'}
                </span>
              </button>
              <div
                className={`merge-matched-list border rounded p-2 mt-1 merge-refined-matched-list ${showMatchedList ? '' : 'd-none'}`}
                id="mergeMatchedList"
              >
                {matchedFields.map((f) => (
                  <div key={f} className="small py-1">
                    {f.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="merge-refined-table-wrap">
            <div id="mergeConflictCategories">
            {visibleConflicts.length > 0 ? (
              <table className="table sh_tablenew mb-0" id="mergeConflictTable">
              <thead>
                <tr>
                  <th style={{ width: "20%" }}>Field</th>
                  <th style={{ width: "28%" }}>Primary Value</th>
                  <th style={{ width: "28%" }}>Secondary Value</th>
                  <th style={{ width: "24%", minWidth: "140px" }}>Resolution</th>
                </tr>
              </thead>
              <tbody>
                {visibleConflicts.map((conflict, index) => (
                  <tr key={index}>
                    <td>
                      <strong>{conflict.label || conflict.field}</strong>
                    </td>
                    <td>
                      {conflict.primary_value || <span className="text-muted">(empty)</span>}
                    </td>
                    <td>
                      {conflict.secondary_value || <span className="text-muted">(empty)</span>}
                    </td>
                    <td
                      style={{
                        minWidth: "140px",
                        backgroundColor:
                          (localResolutions[conflict.field] ?? "primary") === "primary"
                            ? "rgba(212, 237, 218, 0.5)"
                            : "rgba(255, 243, 205, 0.6)",
                      }}
                    >
                      <select
                        className={`form-select form-select-sm merge-resolution-select ${
                          (localResolutions[conflict.field] ?? "primary") === "primary"
                            ? "merge-resolution-primary"
                            : "merge-resolution-secondary"
                        }`}
                        style={{
                          minWidth: "130px",
                          backgroundColor:
                            (localResolutions[conflict.field] ?? "primary") === "primary"
                              ? "#d4edda"
                              : "#fff3cd",
                          borderColor:
                            (localResolutions[conflict.field] ?? "primary") === "primary"
                              ? "#28a745"
                              : "#ffc107",
                          color:
                            (localResolutions[conflict.field] ?? "primary") === "primary"
                              ? "#155724"
                              : "#856404",
                        }}
                        value={localResolutions[conflict.field] ?? "primary"}
                        onChange={(e) => handleResolutionChange(conflict.field, e.target.value)}
                      >
                        <option value="primary" style={{ backgroundColor: "#d4edda", color: "#155724" }}>
                          Keep Primary
                        </option>
                        <option value="secondary" style={{ backgroundColor: "#fff3cd", color: "#856404" }}>
                          Use Secondary
                        </option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Patient visits */}
      <div className="merge-visits-section card border-0 mt-3 merge-refined-visits-card">
        <div className="card-header sh_accordionheader merge-refined-card-head">
          <h6 className="mb-0">Patient visits</h6>
        </div>
        <div className="card-body p-2">
          <p className="small mb-2">
            All visits from the <strong>secondary</strong> patient will be merged into the <strong>primary</strong> chart. Visit history will be combined under the primary record.
          </p>
          <div className="row g-2 small text-muted">
            <div className="col-auto">Primary: <span id="mergePrimaryVisitCount">{primaryVisitCount}</span> visits</div>
            <div className="col-auto">Secondary: <span id="mergeSecondaryVisitCount">{secondaryVisitCount}</span> visits → merge into primary</div>
          </div>
        </div>
      </div>

      {/* Merge consequences */}
      <div className="card border-0 mt-3 merge-refined-consequence">
        <div className="card-header sh_accordionheader"><h6 className="mb-0">Merge consequences</h6></div>
        <div className="card-body p-2">
          <ul className="list-unstyled small mb-0" id="mergeConsequenceList">
            <li className="mb-1"><strong>All visits</strong> from the secondary patient will be moved to the primary patient.</li>
            <li className="mb-1">The secondary patient record will be <strong>deactivated</strong> and marked as <strong>Merged</strong> into the primary.</li>
            <li className="mb-1">If you made a mistake, you can <strong>rollback within 24 hours</strong> from Merge History.</li>
          </ul>
          <p className="small mb-0 mt-2 text-muted">Tip: Always keep the most complete / correct chart as the <strong>primary</strong>.</p>
        </div>
      </div>

      {/* Visit Date Conflicts - same date as primary (must resolve before merge) */}
      {conflicts?.visit_date_conflicts && conflicts.visit_date_conflicts.length > 0 && (
        <div className="card border-0 mt-3 mb-3 border-warning">
          <div className="card-header sh_accordionheader bg-warning bg-opacity-10">
            <h6 className="mb-0 text-dark">
              Visit date conflicts
            </h6>
          </div>
          <div className="card-body p-2">
            <p className="small text-muted mb-2">
              Primary and secondary patients both have visits on the same date(s). Below are the <strong>secondary patient&apos;s visits</strong> on those dates. Choose <strong>Delete</strong> to remove the secondary visit, or <strong>Change date</strong> to move it to another date before merging.
            </p>
            <p className="small text-muted mb-3">
              <em>Delete and Change date will be applied when you click Execute Merge.</em>
            </p>
            {conflicts.visit_date_conflicts.map((conflict) => {
              // Disabled dates: primary visit dates + other conflicts' change_date (MM/DD/YYYY for DatePicker)
              const toMMDDYYYY = (d) => {
                if (!d) return '';
                if (d.includes('/')) return d;
                const [y, m, day] = String(d).split(/[-T]/);
                return m && day ? `${m}/${day}/${y || ''}` : d;
              };
              const primaryDates = (conflicts.primary_visit_dates || []).map(toMMDDYYYY).filter(Boolean);
              const otherChangeDates = conflicts.visit_date_conflicts
                .filter((c) => c.visit_id !== conflict.visit_id)
                .map((c) => visitConflictResolutions[c.visit_id]?.action === 'change_date' && visitConflictResolutions[c.visit_id]?.new_date)
                .filter(Boolean);
              const disabledDates = [...new Set([...primaryDates, ...otherChangeDates])];

              const isMarkedDelete = visitConflictResolutions[conflict.visit_id]?.action === 'delete';
              const isChangeDate = visitConflictResolutions[conflict.visit_id]?.action === 'change_date';

              return (
                <div
                  key={conflict.visit_id}
                  className={`alert py-2 mb-2 d-flex flex-wrap align-items-center gap-2 flex-lg-nowrap ${isMarkedDelete ? 'alert-danger border-danger' : 'alert-warning'}`}
                >
                  <div className="flex-grow-1 small d-flex align-items-center gap-2 flex-wrap">
                    {isMarkedDelete ? (
                      <>
                        <span className="text-decoration-line-through text-muted">
                          <strong>Visit:</strong> {conflict.visit_date} {conflict.visit_time ? `– ${conflict.visit_time}` : ''} {conflict.visit_category ? `(${conflict.visit_category})` : ''}
                        </span>
                        <span className="badge bg-danger">Will be deleted on merge</span>
                      </>
                    ) : isChangeDate ? (
                      <>
                        <span><strong>Visit:</strong> {conflict.visit_date} {conflict.visit_time ? `– ${conflict.visit_time}` : ''} {conflict.visit_category ? `(${conflict.visit_category})` : ''} → <strong>{visitConflictResolutions[conflict.visit_id]?.new_date || ''}</strong></span>
                        <span className="badge bg-info">Date changed</span>
                      </>
                    ) : (
                      <span><strong>Visit:</strong> {conflict.visit_date} {conflict.visit_time ? `– ${conflict.visit_time}` : ''} {conflict.visit_category ? `(${conflict.visit_category})` : ''}</span>
                    )}
                  </div>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <button
                      type="button"
                      className={`btn btn-sm ${isMarkedDelete ? 'btn-danger' : 'btn-outline-danger'}`}
                      onClick={() => onVisitConflictResolved?.(conflict.visit_id, { action: 'delete' })}
                    >
                      {isMarkedDelete ? 'Delete ✓' : 'Delete'}
                    </button>
                    <span className="text-muted small">or</span>
                    <div className="d-flex align-items-center gap-1">
                      <label className="small mb-0">Change date:</label>
                      <div style={{ width: '150px', minWidth: '150px' }}>
                        <FormInputs
                          type="Date"
                          label=""
                          value={isChangeDate ? (visitConflictResolutions[conflict.visit_id]?.new_date || '') : ''}
                          changeHandler={(val) => {
                            if (val) {
                              onVisitConflictResolved?.(conflict.visit_id, { action: 'change_date', new_date: val });
                            }
                          }}
                          placeholder="MM/DD/YYYY"
                          disabledDates={disabledDates}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Visit Conflicts (existing - overlapping/active) */}
      {conflicts?.visit_conflicts && conflicts.visit_conflicts.length > 0 && (
        <div className="mb-4">
          <h6 className="mb-3">Visit Conflicts</h6>
          {conflicts.visit_conflicts.map((conflict, index) => (
            <div key={index} className="alert alert-warning">
              <strong>{conflict.type === 'overlapping_visit' ? 'Overlapping Visit' : 'Active Visits'}</strong>
              <p className="mb-0 mt-2">{conflict.message}</p>
              {conflict.visits && (
                <ul className="mt-2 mb-0">
                  {conflict.visits.map((visit, vIndex) => (
                    <li key={vIndex}>
                      Visit on {visit.visit_date} - Status: {visit.visit_status}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {/* No Conflicts */}
      {(!conflicts?.employee_record_conflicts || conflicts.employee_record_conflicts.length === 0) &&
       (!conflicts?.visit_conflicts || conflicts.visit_conflicts.length === 0) &&
       (!conflicts?.visit_date_conflicts || conflicts.visit_date_conflicts.length === 0) && (
        <div className="alert alert-success">
          <strong>No conflicts detected!</strong> You can proceed with the merge.
        </div>
      )}
    </div>
  );
};

export default ConflictResolutionPanel;

